import {
  BoldOutlined,
  CheckCircleOutlined,
  CloseCircleOutlined,
  DeleteOutlined,
  DownOutlined,
  EditOutlined,
  ItalicOutlined,
  LinkOutlined,
  SaveOutlined,
  SmileOutlined,
  UnderlineOutlined,
  UpOutlined,
} from '@ant-design/icons';
import {
  Button,
  DatePicker,
  Dropdown,
  Input,
  Modal,
  Space,
  Switch,
  Tooltip,
} from 'antd';
import { useBackdropClose } from '../../hooks/useBackdropClose';
import dayjs from 'dayjs';
import React, { useCallback } from 'react';
import {
  getInformerEditorPlainText,
  getTemplateLiveStatus,
  INFORMER_EMOJI_OPTIONS,
} from '../../utils/cabinetMarketingUtils';

const CabinetInformerTab = ({
  sectionDirty,
  sectionSaving,
  handleSaveMarketingSection,
  cabinetInformer,
  cabinetInformerTemplates,
  cabinetInformerPublications,
  cabinetInformerEditorRef,
  informerClearPublicationsLoading,
  informerEditingTemplateId,
  informerTemplateToggleLoadingId,
  informerTemplateDismissibleLoadingId,
  informerTemplateBodyExpanded,
  setInformerTemplateBodyExpanded,
  informerLinkModalOpen,
  setInformerLinkModalOpen,
  informerLinkDraft,
  setInformerLinkDraft,
  informerEmojiPickerOpen,
  informerSaveTemplateModalOpen,
  setInformerSaveTemplateModalOpen,
  informerSaveTemplateNameDraft,
  setInformerSaveTemplateNameDraft,
  handleClearInformerPublicationsClick,
  handleAddInformerTemplate,
  handleUpdateInformerTemplate,
  handleDeleteInformerTemplate,
  handlePersistInformerTemplateRow,
  handleInformerEditorInput,
  captureInformerLinkEditorSelection,
  captureInformerEmojiEditorSelection,
  handleInsertInformerEmoji,
  handleInformerEmojiPickerOpenChange,
  runInformerEditorCommand,
  handleInformerLinkModalOk,
  handleOpenSaveInformerTemplateModal,
  handleSaveInformerTemplateModalOk,
  handleSaveInformerTemplateEdit,
  handleEditInformerTemplate,
  handleTemplateEnabledToggle,
  clearInformerEditorDraft,
}) => {
  const renderInformerEmojiPicker = useCallback(
    () => (
      <div className="sett-informer-emoji-picker" role="listbox" aria-label="Выбор эмодзи">
        {INFORMER_EMOJI_OPTIONS.map((emoji) => (
          <button
            key={emoji}
            type="button"
            className="sett-informer-emoji-picker__item"
            role="option"
            aria-selected="false"
            aria-label={`Вставить эмодзи ${emoji}`}
            onMouseDown={(e) => e.preventDefault()}
            onClick={() => handleInsertInformerEmoji(emoji)}
          >
            {emoji}
          </button>
        ))}
      </div>
    ),
    [handleInsertInformerEmoji],
  );

  const handleEditorKeyDown = (e) => {
    if (e.key !== 'Enter' || e.shiftKey) return;
    e.preventDefault();
    const el = cabinetInformerEditorRef.current;
    if (!el) return;
    try {
      document.execCommand('insertLineBreak');
    } catch (_err) {
      document.execCommand('insertHTML', false, '<br>');
    }
    handleInformerEditorInput();
  };

  const handleLinkModalCancel = () => {
    setInformerLinkModalOpen(false);
    setInformerLinkDraft('');
  };

  const handleSaveTemplateModalCancel = () => {
    setInformerSaveTemplateModalOpen(false);
    setInformerSaveTemplateNameDraft('');
  };

  // Затемнения окон ссылки и сохранения шаблона закрывают только настоящим щелчком (владелец 29.10, DF85)
  const linkModalBackdrop = useBackdropClose(handleLinkModalCancel);
  const saveTemplateModalBackdrop = useBackdropClose(handleSaveTemplateModalCancel);

  return (
    <>
      <div className="sett-card">
        <div className="sett-card__header sett-card__header--row">
          <h3 className="sett-card__title">Информер для клиентов</h3>
          <div className="sett-card__header-actions">
            <Button type="default" onClick={handleAddInformerTemplate}>
              Добавить шаблон
            </Button>
            <Button
              type={sectionDirty.informer ? 'primary' : 'default'}
              disabled={!sectionDirty.informer}
              loading={sectionSaving.informer}
              onClick={() => handleSaveMarketingSection('informer')}
              aria-label="Сохранить информер"
            >
              Сохранить
            </Button>
          </div>
        </div>
        <div className="sett-card__body">
          <p className="sett-cell--muted" style={{ marginBottom: 12 }}>
            Поле ниже — черновик для создания и редактирования шаблонов. Переключатель «Включён» сразу
            показывает или скрывает сообщение у всех клиентов.
          </p>
          <div style={{ marginBottom: 12 }}>
            <Tooltip title="Удаляет из базы все выпуски информера, уже отправленные в ЛК. Шаблоны и черновик редактора не меняются.">
              <Button
                danger
                loading={informerClearPublicationsLoading}
                onClick={handleClearInformerPublicationsClick}
                aria-label="Очистить историю отправленных сообщений информера в личном кабинете"
              >
                Очистить старые сообщения
              </Button>
            </Tooltip>
          </div>
          <div
            className="sett-informer-toolbar"
            style={{ display: 'flex', flexWrap: 'wrap', gap: 8, alignItems: 'center', marginBottom: 8 }}
          >
            <span className="sett-cell--muted">
              {informerEditingTemplateId ? 'Редактирование шаблона:' : 'Черновик шаблона:'}
            </span>
            <Tooltip title="Жирный">
              <Button
                type="default"
                size="small"
                icon={<BoldOutlined />}
                onClick={() => runInformerEditorCommand('bold')}
                aria-label="Жирный текст"
              />
            </Tooltip>
            <Tooltip title="Курсив">
              <Button
                type="default"
                size="small"
                icon={<ItalicOutlined />}
                onClick={() => runInformerEditorCommand('italic')}
                aria-label="Курсив"
              />
            </Tooltip>
            <Tooltip title="Подчёркнутый">
              <Button
                type="default"
                size="small"
                icon={<UnderlineOutlined />}
                onClick={() => runInformerEditorCommand('underline')}
                aria-label="Подчёркнутый текст"
              />
            </Tooltip>
            <Tooltip title="Ссылка">
              <Button
                type="default"
                size="small"
                icon={<LinkOutlined />}
                onMouseDown={(e) => {
                  e.preventDefault();
                  captureInformerLinkEditorSelection();
                }}
                onClick={() => {
                  setInformerLinkDraft('');
                  setInformerLinkModalOpen(true);
                }}
                aria-label="Вставить ссылку"
              />
            </Tooltip>
            <Dropdown
              trigger={['click']}
              placement="bottomLeft"
              open={informerEmojiPickerOpen}
              onOpenChange={handleInformerEmojiPickerOpenChange}
              dropdownRender={renderInformerEmojiPicker}
            >
              <Tooltip title="Эмодзи">
                <Button
                  type="default"
                  size="small"
                  icon={<SmileOutlined />}
                  onMouseDown={(e) => {
                    e.preventDefault();
                    captureInformerEmojiEditorSelection();
                  }}
                  aria-label="Вставить эмодзи"
                  aria-expanded={informerEmojiPickerOpen}
                  aria-haspopup="listbox"
                />
              </Tooltip>
            </Dropdown>
            {informerEditingTemplateId ? (
              <>
                <Button
                  type="primary"
                  size="small"
                  icon={<SaveOutlined />}
                  onClick={handleSaveInformerTemplateEdit}
                  disabled={!getInformerEditorPlainText(cabinetInformer.text)}
                  aria-label="Сохранить изменения шаблона"
                >
                  Сохранить изменения
                </Button>
                <Button
                  type="default"
                  size="small"
                  onClick={clearInformerEditorDraft}
                  aria-label="Отменить редактирование шаблона"
                >
                  Отмена
                </Button>
              </>
            ) : (
              <Tooltip title="Сохранить текущий текст как новый шаблон">
                <Button
                  type="default"
                  size="small"
                  icon={<SaveOutlined />}
                  onClick={handleOpenSaveInformerTemplateModal}
                  disabled={!getInformerEditorPlainText(cabinetInformer.text)}
                  aria-label="Сохранить как шаблон"
                >
                  Сохранить как шаблон
                </Button>
              </Tooltip>
            )}
          </div>
          <div
            id="cabinet-informer-textarea"
            ref={cabinetInformerEditorRef}
            className="sett-informer-editor"
            contentEditable
            suppressContentEditableWarning
            role="textbox"
            aria-multiline="true"
            aria-label="Черновик текста шаблона информера"
            spellCheck
            onKeyDown={handleEditorKeyDown}
            onInput={handleInformerEditorInput}
            onBlur={handleInformerEditorInput}
          />

          {cabinetInformerTemplates.length === 0 && (
            <div className="sett-cell--muted" style={{ marginTop: 16 }}>
              Шаблонов пока нет — напишите текст выше и нажмите «Сохранить как шаблон» или «Добавить шаблон» в
              заголовке.
            </div>
          )}
          {cabinetInformerTemplates.length > 0 && (
            <div style={{ marginTop: 20 }}>
              <div className="sett-cell--muted" style={{ marginBottom: 8 }}>
                Шаблоны — показ в личном кабинете (включённый шаблон видят все клиенты, в том числе новые)
              </div>
              {cabinetInformerTemplates.map((tpl, index) => {
                const templateRowKey = tpl.id || `informer-tpl-${index}`;
                const tplLoadingId = tpl.id || `idx-${index}`;
                const liveStatus = getTemplateLiveStatus(
                  tpl,
                  cabinetInformerPublications,
                  cabinetInformer.enabled,
                );
                const isTemplateBodyExpanded = informerTemplateBodyExpanded[templateRowKey] === true;
                const handleToggleTemplateBody = () => {
                  setInformerTemplateBodyExpanded((prev) => ({
                    ...prev,
                    [templateRowKey]: !isTemplateBodyExpanded,
                  }));
                };
                const handleTemplateBodyKeyDown = (e) => {
                  if (e.key !== 'Enter' && e.key !== ' ') return;
                  e.preventDefault();
                  handleToggleTemplateBody();
                };
                return (
                  <div
                    key={templateRowKey}
                    style={{
                      border: '1px solid var(--pm-border, #e4e7ec)',
                      borderRadius: 8,
                      padding: 12,
                      marginBottom: 12,
                      display: 'flex',
                      flexDirection: 'column',
                      gap: 8,
                    }}
                  >
                    <div
                      style={{
                        display: 'flex',
                        gap: 12,
                        flexWrap: 'wrap',
                        alignItems: 'center',
                        width: '100%',
                      }}
                    >
                      <Input
                        placeholder="Название шаблона"
                        value={tpl.name}
                        onChange={(e) => handleUpdateInformerTemplate(index, { name: e.target.value })}
                        onBlur={() => handlePersistInformerTemplateRow(index)}
                        style={{ flex: '1 1 200px', minWidth: 160, maxWidth: 360 }}
                        aria-label={`Шаблон информера ${index + 1}: название`}
                      />
                      <Space wrap>
                        <Switch
                          checked={tpl.enabled !== false}
                          loading={informerTemplateToggleLoadingId === tplLoadingId}
                          onChange={(checked) => handleTemplateEnabledToggle(index, checked)}
                          aria-label={`Шаблон ${index + 1}: включён`}
                        />
                        <span className="sett-cell--muted">Включён</span>
                        <Switch
                          checked={tpl.dismissible === false}
                          loading={informerTemplateDismissibleLoadingId === tplLoadingId}
                          onChange={(checked) =>
                            handlePersistInformerTemplateRow(index, { dismissible: !checked })
                          }
                          aria-label={`Шаблон ${index + 1}: скрыть крестик закрытия в ЛК`}
                        />
                        <span className="sett-cell--muted">Скрывать крестик в ЛК</span>
                      </Space>
                      <div
                        style={{
                          marginLeft: 'auto',
                          display: 'flex',
                          gap: 8,
                          flexWrap: 'wrap',
                          alignItems: 'center',
                        }}
                      >
                        <Button
                          type="default"
                          icon={<EditOutlined />}
                          onClick={() => handleEditInformerTemplate(index)}
                          aria-label={`Редактировать шаблон ${index + 1}`}
                        >
                          Редактировать
                        </Button>
                        <Tooltip
                          title={
                            liveStatus === 'active'
                              ? 'Сообщение показывается всем клиентам в личном кабинете'
                              : 'Сообщение не показывается (выключено, нет текста или вне периода)'
                          }
                        >
                          <span
                            className={`sett-informer-template-status sett-informer-template-status--${liveStatus}`}
                            aria-label={
                              liveStatus === 'active'
                                ? 'Показывается в ЛК'
                                : 'Не показывается в ЛК'
                            }
                          >
                            {liveStatus === 'active' ? (
                              <CheckCircleOutlined />
                            ) : (
                              <CloseCircleOutlined />
                            )}
                          </span>
                        </Tooltip>
                        <Button
                          danger
                          icon={<DeleteOutlined />}
                          onClick={() => handleDeleteInformerTemplate(index)}
                          aria-label="Удалить шаблон"
                        >
                          Удалить
                        </Button>
                        <Tooltip title={isTemplateBodyExpanded ? 'Свернуть текст и период' : 'Развернуть текст и период'}>
                          <Button
                            type="default"
                            icon={isTemplateBodyExpanded ? <UpOutlined /> : <DownOutlined />}
                            onClick={handleToggleTemplateBody}
                            onKeyDown={handleTemplateBodyKeyDown}
                            aria-expanded={isTemplateBodyExpanded}
                            aria-controls={`informer-template-body-${templateRowKey}`}
                            aria-label={
                              isTemplateBodyExpanded
                                ? `Свернуть содержимое шаблона ${index + 1}`
                                : `Развернуть содержимое шаблона ${index + 1}`
                            }
                          />
                        </Tooltip>
                      </div>
                    </div>
                    {isTemplateBodyExpanded && (
                      <div
                        id={`informer-template-body-${templateRowKey}`}
                        style={{ display: 'flex', flexDirection: 'column', gap: 8 }}
                      >
                        <Input.TextArea
                          rows={2}
                          value={tpl.text}
                          onChange={(e) => handleUpdateInformerTemplate(index, { text: e.target.value })}
                          onBlur={() => handlePersistInformerTemplateRow(index)}
                          placeholder="HTML шаблона (те же теги, что и в основном поле)"
                          aria-label={`Шаблон информера ${index + 1}: текст`}
                        />
                        <div style={{ maxWidth: 480 }}>
                          <label className="sett-cell--muted">Период действия шаблона (необязательно)</label>
                          <DatePicker.RangePicker
                            showTime
                            style={{ width: '100%', marginTop: 4 }}
                            value={[
                              tpl.starts_at ? dayjs(tpl.starts_at) : null,
                              tpl.ends_at ? dayjs(tpl.ends_at) : null,
                            ]}
                            onChange={(range) => {
                              const starts = range && range[0] ? range[0].toISOString() : null;
                              const ends = range && range[1] ? range[1].toISOString() : null;
                              handlePersistInformerTemplateRow(index, { starts_at: starts, ends_at: ends });
                            }}
                            aria-label={`Шаблон ${index + 1}: период действия`}
                          />
                        </div>
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </div>

      <Modal
        className="light-modal"
        title="Вставить ссылку"
        open={informerLinkModalOpen}
        onOk={handleInformerLinkModalOk}
        onCancel={handleLinkModalCancel}
        maskClosable={false}
        wrapProps={linkModalBackdrop}
        okText="Вставить"
        cancelText="Отмена"
        destroyOnClose
      >
        <p className="sett-cell--muted" style={{ marginBottom: 8 }}>
          Укажите адрес. Если текст не выделен в поле выше, будет вставлено слово «ссылка».
        </p>
        <Input
          placeholder="https://example.com"
          value={informerLinkDraft}
          onChange={(e) => setInformerLinkDraft(e.target.value)}
          onPressEnter={handleInformerLinkModalOk}
          aria-label="URL ссылки для информера"
        />
      </Modal>

      <Modal
        className="light-modal"
        title="Сохранить как шаблон"
        open={informerSaveTemplateModalOpen}
        onOk={handleSaveInformerTemplateModalOk}
        onCancel={handleSaveTemplateModalCancel}
        maskClosable={false}
        wrapProps={saveTemplateModalBackdrop}
        okText="Добавить шаблон"
        cancelText="Отмена"
        destroyOnClose
      >
        <p className="sett-cell--muted" style={{ marginBottom: 8 }}>
          В шаблон копируется текущее содержимое редактора (с оформлением). После сохранения редактор
          очистится — включите «Включён» в карточке шаблона, чтобы показать сообщение.
        </p>
        <Input
          placeholder="Например: Предупреждение о БПЛА"
          value={informerSaveTemplateNameDraft}
          onChange={(e) => setInformerSaveTemplateNameDraft(e.target.value)}
          onPressEnter={handleSaveInformerTemplateModalOk}
          aria-label="Название нового шаблона информера"
        />
      </Modal>
    </>
  );
};

export default CabinetInformerTab;
